import { useCallback, useMemo, useState, type FormEvent } from "react";
import { AlertTriangle, CalendarClock, Flag, FileCheck2, RotateCcw, ShieldAlert } from "lucide-react";
import type { PlatformAction, SessionView } from "@control-plane/contracts";
import { PageHeader } from "../components/PageHeader";
import { RemotePageState } from "../components/RemotePageState";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { Dialog } from "../components/Overlay";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { InlineFeedback } from "../components/Feedback";
import { V3Select, type SelectOption } from "../components/V3Select";
import { Wizard, type WizardStep } from "../components/Wizard";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { operationsCapabilities } from "../lib/pageCapabilities";

type TenantOption = {
  id?: string;
  name: string;
  status?: string | null;
  selectionToken?: string | null;
  tenantSelectionToken?: string | null;
  tenantGroupSelectionToken?: string | null;
  allowListSelectionToken?: string | null;
};
type SelectionOption = {
  selectionToken: string;
  label: string;
  summary: string;
  kind?: string;
  disabled?: boolean;
  disabledReason?: string;
};
type OperationsSelectors = {
  tenantSelections: SelectionOption[];
  tenantGroups: SelectionOption[];
  allowLists: SelectionOption[];
  owners: SelectionOption[];
  fallbackOwners: SelectionOption[];
};
type OperationDirectoryItem = {
  operationType: string;
  label: string;
  category: string;
  description: string;
  disabled: boolean;
  unavailableReason: string;
};
type FeatureFlagApprovalPayload = {
  catalogKey: string;
  environment: string;
  scope?: Record<string, unknown>;
  schedule?: { plannedAt: string; autoExpireAt?: string };
  impactPreviewToken?: string;
  rollbackPlan?: Record<string, unknown>;
  reason?: string;
};
type FeatureFlagExecutionApproval = {
  id: string;
  operationType: "feature_flag.schedule_change" | "feature_flag.rollback";
  label: string;
  summary: string;
  statusText: string;
  requestedByName: string;
  approvedByName: string;
  expiresAt: string;
  impact: string[];
  recovery: string;
  payload: FeatureFlagApprovalPayload;
  unavailableReason: string;
};
type ApprovalConsole = { operations: OperationDirectoryItem[]; pendingCount: number; executionApprovals: FeatureFlagExecutionApproval[] };
type IncidentView = {
  id: string;
  title: string;
  severity: string;
  status: string;
  ownerName: string;
  scopeSummary: string;
  timelineSummary: string;
  recoveryPlan: string;
  postmortem: string;
};
type MaintenanceView = {
  id: string;
  title: string;
  status: string;
  windowText: string;
  scopeSummary: string;
  notificationPlan: string;
  cancellation: string;
  earlyEnd: string;
};
type FeatureFlagView = {
  id: string;
  catalogKey: string;
  scheduleSelectionToken: string;
  rollbackSelectionToken: string;
  label: string;
  description: string;
  status: string;
  environment: string;
  scopeSummary: string;
  tenantNames: string[];
  rolloutPercent: number;
  plannedAt: string;
  autoExpireAt: string;
  rollbackPlan: string;
};
type FeatureFlagCatalogView = {
  catalogKey: string;
  scheduleSelectionToken: string;
  rollbackSelectionToken: string;
  label: string;
  description: string;
  environment: string;
};
type OperationsConsole = {
  incidents: IncidentView[];
  maintenance: MaintenanceView[];
  flags: FeatureFlagView[];
  tenants: TenantOption[];
  selectors: OperationsSelectors;
};
type Feedback = { tone: "success" | "danger"; text: string } | null;
type IncidentStatusState = { incident: IncidentView; status: "monitoring" | "resolved" } | null;
type MaintenanceActionState = { item: MaintenanceView; mode: "cancel" | "end-early" } | null;
type FlagWizardMode = "schedule" | "rollback";
type FlagWizardState = { mode: FlagWizardMode; initialFlag: FeatureFlagView | null } | null;
type FlagExecutionState = FeatureFlagExecutionApproval | null;
type FlagDraft = {
  operationType: string;
  flagCatalogKey: string;
  environment: string;
  scopeMode: "tenant_group" | "allow_list" | "all_tenants";
  scopeSelectionToken: string;
  rolloutPercent: string;
  plannedAt: string;
  autoExpireAt: string;
  reason: string;
  preview: ApprovalPreview | null;
  previewLoading: boolean;
  previewError: string;
  submitting: boolean;
  submitError: string;
};
type ApprovalPreview = {
  impact?: string[];
  recovery?: string;
  canonicalBusinessInputs?: Record<string, unknown>;
  idempotencyKey?: string;
  impactPreviewToken?: string;
};

const severityLabels: Record<string, string> = { sev1: "严重事故", sev2: "重要事故", sev3: "一般事故", sev4: "轻微事故" };
const severityOptions: SelectOption[] = [
  { value: "sev1", label: "严重事故", hint: "客户核心链路不可用，需要立刻指挥恢复。" },
  { value: "sev2", label: "重要事故", hint: "影响关键能力或多个企业，需要持续跟进。" },
  { value: "sev3", label: "一般事故", hint: "局部影响，可按值班流程处理。" },
  { value: "sev4", label: "轻微事故", hint: "轻微波动，记录并观察即可。" },
];
const incidentStatusLabels: Record<string, string> = { open: "处理中", monitoring: "观察中", resolved: "已恢复", closed: "已关闭" };
const maintenanceStatusLabels: Record<string, string> = { scheduled: "已计划", active: "进行中", completed: "已完成", canceled: "已取消" };
const environmentOptions: SelectOption[] = [
  { value: "production", label: "生产环境", hint: "真实客户使用的正式环境。" },
  { value: "staging", label: "预发布环境", hint: "上线前验证环境。" },
  { value: "development", label: "开发环境", hint: "内部验证环境。" },
];
const scopeOptions: SelectOption[] = [
  { value: "tenant_group", label: "租户组", hint: "按平台维护的企业分组发布。" },
  { value: "allow_list", label: "名单", hint: "只对选中的企业名单发布。" },
  { value: "all_tenants", label: "全部企业", hint: "影响面最大，需先看预览与审批。" },
];
const rolloutOptions: SelectOption[] = [
  { value: "10", label: "10%" },
  { value: "25", label: "25%" },
  { value: "50", label: "50%" },
  { value: "100", label: "100%" },
  { value: "0", label: "关闭" },
];
const emptyOperationsSelectors: OperationsSelectors = {
  tenantSelections: [],
  tenantGroups: [],
  allowLists: [],
  owners: [],
  fallbackOwners: [],
};

function hasAction(session: SessionView, action: PlatformAction | string): boolean {
  return (session.actions as readonly string[]).includes(action);
}

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stringOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function arrayOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function isCodeLike(value: string): boolean {
  return /^[a-z0-9][a-z0-9_.:-]*$/i.test(value.trim());
}

function safeText(value: unknown, fallback: string): string {
  const text = stringOf(value);
  if (!text) return fallback;
  const normalized = text.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, "所选企业");
  if (isCodeLike(normalized)) return fallback;
  return normalized.slice(0, 220);
}

function isOpaqueSelectionToken(value: string | null): value is string {
  if (!value) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) return false;
  return /^(opssel|apvsel)_[A-Za-z0-9_-]{8,}$/.test(value);
}

function isOpsImpactPreviewToken(value: string | null): value is string {
  return Boolean(value && /^opsprev_[A-Za-z0-9_-]{8,}$/.test(value));
}

function isCatalogKey(value: string | null): value is string {
  return Boolean(value && /^[a-z0-9][a-z0-9.-]{2,119}$/.test(value));
}

function normalizeSelectionOption(value: unknown, fallbackLabel: string, kind?: string): SelectionOption | null {
  const record = recordOf(value);
  const token = stringOf(record.selectionToken) ?? stringOf(record.token) ?? stringOf(record.value);
  if (!isOpaqueSelectionToken(token)) return null;
  const optionKind = stringOf(record.kind ?? record.type) ?? kind;
  return {
    selectionToken: token,
    label: safeText(record.label ?? record.name ?? record.displayName, fallbackLabel),
    summary: safeText(record.summary ?? record.description ?? record.hint, kind ? `${fallbackLabel} · ${kind}` : fallbackLabel),
    disabled: Boolean(record.disabled),
    disabledReason: safeText(record.disabledReason ?? record.unavailableReason, ""),
    ...(optionKind ? { kind: optionKind } : {}),
  };
}

function uniqueSelectionOptions(items: SelectionOption[]): SelectionOption[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.selectionToken)) return false;
    seen.add(item.selectionToken);
    return true;
  });
}

function selectionListFrom(record: Record<string, unknown>, keys: string[], fallbackLabel: string, kind?: string): SelectionOption[] {
  return uniqueSelectionOptions(keys.flatMap((key) => arrayOf(record[key]).map((item) => normalizeSelectionOption(item, fallbackLabel, kind)).filter((item): item is SelectionOption => Boolean(item))));
}

function normalizeOperationsSelectors(data: Record<string, unknown>, tenantItems: TenantOption[]): OperationsSelectors {
  const nested = recordOf(data.selectors ?? data.businessSelectors ?? data.operationSelectors);
  const fromTop = (keys: string[], fallback: string, kind?: string) => [
    ...selectionListFrom(nested, keys, fallback, kind),
    ...selectionListFrom(data, keys, fallback, kind),
  ];
  const tenantSelections = fromTop(["tenantSelections", "tenantOptions", "tenantScopeOptions"], "企业范围", "tenant_selection");
  const tenantGroups = fromTop(["tenantGroups", "tenantGroupOptions", "tenantGroupSelections"], "租户组", "tenant_group");
  const allowLists = fromTop(["allowLists", "allowListOptions", "allowListSelections"], "名单", "allow_list");
  const owners = fromTop(["owners", "ownerOptions", "ownerSelections"], "负责人", "owner");
  const fallbackOwners = fromTop(["fallbackOwners", "fallbackOwnerOptions", "fallbackOwnerSelections"], "备用负责人", "owner");
  const tenantDerived = tenantItems.map((tenant) => normalizeSelectionOption({
    selectionToken: tenant.tenantSelectionToken ?? tenant.selectionToken,
    label: tenant.name,
    summary: tenant.status === "archived" ? "已关闭企业不可选择" : "企业范围",
    disabled: tenant.status === "archived",
  }, "企业范围", "tenant_selection")).filter((item): item is SelectionOption => Boolean(item));
  const allowListDerived = tenantItems.map((tenant) => normalizeSelectionOption({
    selectionToken: tenant.allowListSelectionToken,
    label: tenant.name,
    summary: tenant.status === "archived" ? "已关闭企业不可选择" : "名单",
    disabled: tenant.status === "archived",
  }, "名单", "allow_list")).filter((item): item is SelectionOption => Boolean(item));
  const groupDerived = tenantItems.map((tenant) => normalizeSelectionOption({
    selectionToken: tenant.tenantGroupSelectionToken,
    label: tenant.name,
    summary: tenant.status === "archived" ? "已关闭企业不可选择" : "租户组",
    disabled: tenant.status === "archived",
  }, "租户组", "tenant_group")).filter((item): item is SelectionOption => Boolean(item));
  return {
    tenantSelections: uniqueSelectionOptions([...tenantSelections, ...tenantDerived]),
    tenantGroups: uniqueSelectionOptions([...tenantGroups, ...groupDerived]),
    allowLists: uniqueSelectionOptions([...allowLists, ...allowListDerived]),
    owners: uniqueSelectionOptions(owners),
    fallbackOwners: uniqueSelectionOptions(fallbackOwners.length ? fallbackOwners : owners),
  };
}

function statusLabel(labels: Record<string, string>, value: string, fallback: string): string {
  return labels[value] ?? (isCodeLike(value) ? fallback : value);
}

function formatDateTime(value: unknown, fallback = "时间待确认"): string {
  const text = stringOf(value);
  if (!text) return fallback;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function environmentLabel(value: string): string {
  return statusLabel({ production: "生产环境", staging: "预发布环境", development: "开发环境" }, value, "环境待确认");
}

function toIsoFromLocal(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

function localDateTime(hoursFromNow: number): string {
  const date = new Date(Date.now() + hoursFromNow * 60 * 60 * 1000);
  date.setSeconds(0, 0);
  return date.toISOString().slice(0, 16);
}

function createRequestKey(prefix: string): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${suffix}`.slice(0, 160);
}

function scopeSummary(record: Record<string, unknown>, fallback: string): string {
  const direct = safeText(record.businessScope ?? record.scopeSummary, "");
  if (direct) return direct;
  return fallback;
}

function normalizeTenantOption(value: unknown): TenantOption | null {
  const record = recordOf(value);
  const name = stringOf(record.name ?? record.label);
  if (!name) return null;
  const id = stringOf(record.id);
  const status = stringOf(record.status);
  const selectionToken = stringOf(record.selectionToken);
  const tenantSelectionToken = stringOf(record.tenantSelectionToken);
  const tenantGroupSelectionToken = stringOf(record.tenantGroupSelectionToken);
  const allowListSelectionToken = stringOf(record.allowListSelectionToken);
  return {
    name,
    ...(id ? { id } : {}),
    ...(status ? { status } : {}),
    ...(selectionToken ? { selectionToken } : {}),
    ...(tenantSelectionToken ? { tenantSelectionToken } : {}),
    ...(tenantGroupSelectionToken ? { tenantGroupSelectionToken } : {}),
    ...(allowListSelectionToken ? { allowListSelectionToken } : {}),
  };
}

function scopedFlagToken(record: Record<string, unknown>, operation: "schedule" | "rollback", environment: string): string {
  const directKey = operation === "schedule" ? "scheduleSelectionToken" : "rollbackSelectionToken";
  const legacySchedule = operation === "schedule" ? stringOf(record.selectionToken) ?? stringOf(record.approvalSelectionToken) : null;
  const nested = [
    recordOf(record.selectionTokens),
    recordOf(record.approvalSelectionTokens),
    recordOf(record.selectionTokenByOperation),
    recordOf(record.approvalSelectionTokenByOperation),
  ];
  const environmentNested = [
    recordOf(recordOf(record.environmentTokens)[environment]),
    recordOf(recordOf(record.tokensByEnvironment)[environment]),
    recordOf(recordOf(record.approvalTokensByEnvironment)[environment]),
  ];
  const token = stringOf(record[directKey])
    ?? stringOf(record[operation])
    ?? environmentNested.map((item) => stringOf(item[directKey]) ?? stringOf(item[operation])).find((item): item is string => Boolean(item))
    ?? nested.map((item) => stringOf(item[directKey]) ?? stringOf(item[operation])).find((item): item is string => Boolean(item))
    ?? legacySchedule;
  return isOpaqueSelectionToken(token) ? token : "";
}

function normalizeFeatureFlagCatalog(value: unknown): FeatureFlagCatalogView[] {
  const record = recordOf(value);
  const catalogKey = stringOf(record.catalogKey);
  if (!isCatalogKey(catalogKey)) return [];
  const environments = arrayOf(record.environments).filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  const environmentList = environments.length ? environments : [stringOf(record.environment) ?? "production"];
  return environmentList.map((environment) => ({
    catalogKey,
    scheduleSelectionToken: scopedFlagToken(record, "schedule", environment),
    rollbackSelectionToken: scopedFlagToken(record, "rollback", environment),
    label: safeText(record.label ?? record.name, "功能名称待补充"),
    description: safeText(record.description, "该功能尚未完成配置，需从正式目录发起首次发布。"),
    environment,
  }));
}

function catalogProjection(catalog: FeatureFlagCatalogView): FeatureFlagView {
  return {
    id: `catalog-${catalog.catalogKey}-${catalog.environment}`,
    catalogKey: catalog.catalogKey,
    scheduleSelectionToken: catalog.scheduleSelectionToken,
    rollbackSelectionToken: catalog.rollbackSelectionToken,
    label: catalog.label,
    description: catalog.description,
    status: "unconfigured",
    environment: catalog.environment,
    scopeSummary: "尚无发布范围",
    tenantNames: [],
    rolloutPercent: 0,
    plannedAt: "尚无发布计划",
    autoExpireAt: "尚无自动过期",
    rollbackPlan: "首次发布前暂无可回滚版本。",
  };
}

function normalizeOperationsConsole(data: unknown): OperationsConsole {
  const record = recordOf(data);
  const tenantItems = arrayOf(record.tenants).map(normalizeTenantOption).filter((item): item is TenantOption => Boolean(item));
  const catalogItems = arrayOf(record.featureFlagCatalog).flatMap(normalizeFeatureFlagCatalog);
  const catalogByScope = new Map(catalogItems.map((item) => [`${item.catalogKey}:${item.environment}`, item]));
  const catalogByLabelAndScope = new Map(catalogItems.map((item) => [`${item.label}:${item.environment}`, item]));
  const flagRows = arrayOf(record.flags).map((item) => normalizeFlag(item, catalogByScope, catalogByLabelAndScope));
  const configuredKeys = new Set(flagRows.map((item) => `${item.catalogKey}:${item.environment}`));
  const catalogRows = catalogItems
    .map(catalogProjection)
    .filter((item) => !configuredKeys.has(`${item.catalogKey}:${item.environment}`));
  return {
    incidents: arrayOf(record.incidents).map(normalizeIncident),
    maintenance: arrayOf(record.maintenance).map(normalizeMaintenance),
    flags: [...flagRows, ...catalogRows],
    tenants: tenantItems,
    selectors: normalizeOperationsSelectors(record, tenantItems),
  };
}

function normalizeIncident(value: unknown): IncidentView {
  const record = recordOf(value);
  const timeline = arrayOf(record.timeline).map((item) => {
    const step = recordOf(item);
    return `${formatDateTime(step.at, "时间待补充")}：${safeText(step.note, "处置记录待补充")}`;
  });
  return {
    id: stringOf(record.id) ?? createRequestKey("incident-row"),
    title: safeText(record.title, "事故名称待补充"),
    severity: stringOf(record.severity) ?? "sev3",
    status: stringOf(record.status) ?? "open",
    ownerName: safeText(record.ownerName ?? record.owner, "待指派负责人"),
    scopeSummary: scopeSummary(record, "影响范围待确认"),
    timelineSummary: timeline[0] ?? safeText(record.timelineSummary, "处置时间线待补充"),
    recoveryPlan: safeText(record.recoveryPlan ?? record.recovery, "恢复计划待补充"),
    postmortem: safeText(record.postmortemSummary ?? record.postmortem, "复盘要求待补充"),
  };
}

function normalizeMaintenance(value: unknown): MaintenanceView {
  const record = recordOf(value);
  return {
    id: stringOf(record.id) ?? createRequestKey("maintenance-row"),
    title: safeText(record.title, "维护计划待补充"),
    status: stringOf(record.status) ?? "scheduled",
    windowText: `${formatDateTime(record.startsAt)} 至 ${formatDateTime(record.endsAt)}`,
    scopeSummary: scopeSummary(record, "影响企业待确认"),
    notificationPlan: safeText(record.notificationPlan ?? record.message, "通知计划待补充"),
    cancellation: safeText(record.cancellation, "可在窗口开始前取消维护。"),
    earlyEnd: safeText(record.earlyEnd, "恢复验证完成后可提前结束。"),
  };
}

function normalizeFlag(value: unknown, catalogByScope: Map<string, FeatureFlagCatalogView> = new Map(), catalogByLabelAndScope: Map<string, FeatureFlagCatalogView> = new Map()): FeatureFlagView {
  const record = recordOf(value);
  const configRecord = recordOf(record.config);
  const directCatalogKey = stringOf(record.catalogKey);
  const environment = stringOf(record.environment) ?? "production";
  const directLabel = safeText(record.catalogLabel ?? record.label ?? record.displayName ?? configRecord.label, "功能名称待补充");
  const catalog = (directCatalogKey ? catalogByScope.get(`${directCatalogKey}:${environment}`) : null) ?? catalogByLabelAndScope.get(`${directLabel}:${environment}`) ?? null;
  const rollout = Number(record.rolloutPercent);
  const status = stringOf(record.status) ?? (typeof record.enabled === "boolean" ? (record.enabled ? "enabled" : "disabled") : "unconfigured");
  return {
    id: stringOf(record.id) ?? createRequestKey("flag-row"),
    catalogKey: isCatalogKey(directCatalogKey) ? directCatalogKey : catalog?.catalogKey ?? "",
    scheduleSelectionToken: scopedFlagToken(record, "schedule", environment) || catalog?.scheduleSelectionToken || "",
    rollbackSelectionToken: scopedFlagToken(record, "rollback", environment) || catalog?.rollbackSelectionToken || "",
    label: catalog?.label ?? directLabel,
    description: catalog?.description ?? safeText(record.description ?? configRecord.description, "按正式目录管理发布计划、自动过期和回滚。"),
    status,
    environment,
    scopeSummary: safeText(record.scopeSummary ?? record.businessScope, "发布范围待确认"),
    tenantNames: [],
    rolloutPercent: Number.isFinite(rollout) ? rollout : 0,
    plannedAt: formatDateTime(record.plannedAt, "计划时间待确认"),
    autoExpireAt: formatDateTime(record.autoExpireAt ?? record.expiresAt, "自动过期时间待确认"),
    rollbackPlan: safeText(record.rollbackPlan, "可回滚到上一发布比例。"),
  };
}

function normalizeFeatureFlagApprovalPayload(value: unknown, operationType: FeatureFlagExecutionApproval["operationType"]): FeatureFlagApprovalPayload | null {
  const payload = recordOf(value);
  const catalogKey = stringOf(payload.catalogKey);
  if (!isCatalogKey(catalogKey)) return null;
  const environment = stringOf(payload.environment) ?? "production";
  const scheduleRecord = recordOf(payload.schedule);
  const plannedAt = stringOf(scheduleRecord.plannedAt ?? payload.plannedAt);
  const autoExpireAt = stringOf(scheduleRecord.autoExpireAt ?? payload.autoExpireAt);
  const rollbackPlan = recordOf(payload.rollbackPlan);
  const result: FeatureFlagApprovalPayload = {
    catalogKey,
    environment,
    rollbackPlan: Object.keys(rollbackPlan).length ? rollbackPlan : { mode: "restore_previous" },
  };
  if (operationType === "feature_flag.schedule_change") {
    const impactPreviewToken = stringOf(payload.impactPreviewToken);
    const scope = recordOf(payload.scope);
    if (Object.keys(scope).length) result.scope = scope;
    if (plannedAt) result.schedule = { plannedAt, ...(autoExpireAt ? { autoExpireAt } : {}) };
    if (isOpsImpactPreviewToken(impactPreviewToken)) result.impactPreviewToken = impactPreviewToken;
  } else {
    const reason = stringOf(payload.reason);
    if (reason) result.reason = reason;
  }
  return result;
}

function normalizeFeatureFlagExecutionApproval(value: unknown, options: { currentOperatorId: string; requesterOnlyList: boolean }): FeatureFlagExecutionApproval | null {
  const record = recordOf(value);
  const id = stringOf(record.id);
  const operationType = stringOf(record.operationType);
  if (!id || (operationType !== "feature_flag.schedule_change" && operationType !== "feature_flag.rollback")) return null;
  if (stringOf(record.status) !== "approved") return null;
  if (stringOf(record.executedAt)) return null;
  const expiresAtRaw = stringOf(record.expiresAt);
  if (expiresAtRaw && new Date(expiresAtRaw).getTime() <= Date.now()) return null;
  const requester = stringOf(record.requestedBy);
  const isSelfRequested = Boolean(record.isSelfRequested) || (requester ? requester === options.currentOperatorId : options.requesterOnlyList);
  if (!isSelfRequested) return null;
  const payload = normalizeFeatureFlagApprovalPayload(record.payload, operationType);
  if (!payload) return null;
  const expectedTarget = `${payload.catalogKey}:${payload.environment}`;
  const storedTarget = stringOf(recordOf(record.target).featureFlagKey);
  const targetMismatch = storedTarget && storedTarget !== expectedTarget && storedTarget !== payload.catalogKey;
  const unavailable = [
    targetMismatch ? "审批凭证与当前功能目录不一致，请重新发起。" : "",
    operationType === "feature_flag.schedule_change" && !payload.impactPreviewToken ? "缺少影响预览凭证，请重新生成影响预览并发起审批。" : "",
    operationType === "feature_flag.schedule_change" && (!payload.scope || !payload.schedule) ? "审批内容缺少发布范围或计划时间，请重新发起。" : "",
  ].find(Boolean) ?? "";
  const impact = arrayOf(record.impact).map((item) => safeText(item, "")).filter(Boolean);
  return {
    id,
    operationType,
    label: operationType === "feature_flag.rollback" ? "执行回滚" : "执行发布",
    summary: safeText(record.targetSummary ?? record.targetLabel, operationType === "feature_flag.rollback" ? "已批准的功能回滚" : "已批准的功能发布"),
    statusText: expiresAtRaw ? `有效期至 ${formatDateTime(expiresAtRaw)}` : "有效期待确认",
    requestedByName: safeText(record.requestedByName, "申请人"),
    approvedByName: safeText(record.approvedByName, "审批人"),
    expiresAt: expiresAtRaw ?? "",
    impact: impact.length ? impact : ["影响范围以审批预览为准。"],
    recovery: safeText(record.recovery, "如执行异常，请按恢复计划重新发起审批或回滚。"),
    payload,
    unavailableReason: unavailable,
  };
}

function normalizeApprovalConsole(directory: unknown, approvals: unknown, options: { currentOperatorId: string; requesterOnlyList: boolean }): ApprovalConsole {
  const items = arrayOf(recordOf(directory).items)
    .map((item) => {
      const record = recordOf(item);
      const operationType = stringOf(record.operationType) ?? "";
      return {
        operationType,
        label: safeText(record.label, "审批动作"),
        category: stringOf(record.category) ?? "",
        description: safeText(record.description, "发起前需查看影响预览。"),
        disabled: Boolean(record.disabled),
        unavailableReason: safeText(record.unavailableReason, "暂不可发起。"),
      };
    })
    .filter((item) => item.category === "operations" || item.operationType.startsWith("incident.") || item.operationType.startsWith("maintenance.") || item.operationType.startsWith("feature_flag."));
  const approvalItems = arrayOf(recordOf(approvals).items);
  const pendingCount = approvalItems.filter((item) => stringOf(recordOf(item).status) === "pending").length;
  const executionApprovals = approvalItems
    .map((item) => normalizeFeatureFlagExecutionApproval(item, options))
    .filter((item): item is FeatureFlagExecutionApproval => Boolean(item));
  return { operations: items, pendingCount, executionApprovals };
}

function operationOptions(operations: OperationDirectoryItem[], mode: FlagWizardMode): SelectOption[] {
  const allowed = mode === "rollback"
    ? ["feature_flag.rollback"]
    : ["feature_flag.schedule_change", "feature_flag.adjust", "feature_flag.full_publish"];
  return operations.filter((operation) => allowed.includes(operation.operationType)).map((operation) => ({
    value: operation.operationType,
    label: operation.label,
    hint: operation.disabled ? operation.unavailableReason : operation.description,
    disabled: operation.disabled,
  }));
}

function flagChoiceValue(flag: FeatureFlagView): string {
  return `${flag.catalogKey}::${flag.environment}`;
}

function flagSelectionTokenFor(flag: FeatureFlagView | null, operationType: string): string {
  if (!flag) return "";
  return operationType === "feature_flag.rollback" ? flag.rollbackSelectionToken : flag.scheduleSelectionToken;
}

function firstSelectableFlagValue(flags: FeatureFlagView[], operationType: string): string {
  const first = flags.find((flag) => flag.catalogKey && flagSelectionTokenFor(flag, operationType));
  return first ? flagChoiceValue(first) : "";
}

function flagOptions(flags: FeatureFlagView[], operationType: string): SelectOption[] {
  return flags.filter((flag) => flag.catalogKey).map((flag) => {
    const token = flagSelectionTokenFor(flag, operationType);
    return {
      value: flagChoiceValue(flag),
      label: `${flag.label} · ${environmentLabel(flag.environment)}`,
      hint: token ? `${flag.scopeSummary}` : "等待平台签发该动作的选择凭证",
      disabled: !token,
    };
  });
}

function selectorOptions(items: SelectionOption[], emptyLabel: string): SelectOption[] {
  return items.length
    ? items.map((item) => ({
      value: item.selectionToken,
      label: item.label,
      hint: item.disabled ? item.disabledReason || "暂不可选择" : item.summary,
      ...(item.disabled ? { disabled: true } : {}),
    }))
    : [{ value: "", label: emptyLabel, hint: "等待平台签发选择凭证", disabled: true }];
}

function firstSelectableToken(items: SelectionOption[]): string {
  return items.find((item) => !item.disabled)?.selectionToken ?? "";
}

function scopeSelectorItems(mode: FlagDraft["scopeMode"], selectors: OperationsSelectors): SelectionOption[] {
  if (mode === "allow_list") return selectors.allowLists.length ? selectors.allowLists : selectors.tenantSelections;
  return selectors.tenantGroups;
}

function scopePayload(scopeMode: FlagDraft["scopeMode"], selectionToken: string, rolloutPercent: number, selectors: OperationsSelectors) {
  const items = scopeSelectorItems(scopeMode, selectors);
  const selected = items.find((item) => item.selectionToken === selectionToken) ?? null;
  if (!selected) return null;
  const summary = selected.label;
  const base = {
    services: ["platform"],
    blastRadius: scopeMode === "all_tenants" ? "all_tenants" : "single_tenant",
    summary,
    rolloutPercent,
  };
  if (scopeMode === "allow_list") {
    if (selected.kind === "tenant_selection") {
      return { ...base, tenantSelectionTokens: [selectionToken], tenantGroupSelectionTokens: [], allowListSelectionTokens: [] };
    }
    return { ...base, tenantSelectionTokens: [], tenantGroupSelectionTokens: [], allowListSelectionTokens: [selectionToken] };
  }
  return { ...base, tenantSelectionTokens: [], tenantGroupSelectionTokens: [selectionToken], allowListSelectionTokens: [] };
}

function operationBadgeValue(status: string): string {
  if (status === "resolved" || status === "completed") return "healthy";
  if (status === "enabled") return "active";
  if (status === "disabled" || status === "canceled") return "suspended";
  if (status === "scheduled") return "pending";
  if (status === "failed") return "failed";
  if (status === "expired" || status === "rolled_back") return "suspended";
  return status;
}

function flagStatusText(item: FeatureFlagView): string {
  if (item.status === "unconfigured") return "未配置";
  if (item.status === "enabled") return "已发布";
  if (item.status === "disabled") return "已关闭";
  return statusLabel({ scheduled: "已计划", failed: "发布失败", expired: "已过期", rolled_back: "已回滚", pending: "待审批", draft: "待发布" }, item.status, "状态待确认");
}

function flagRolloutText(item: FeatureFlagView): string {
  if (item.status === "unconfigured") return "尚无发布";
  if (item.status === "disabled") return "关闭";
  return `${item.rolloutPercent}%`;
}

function approvalCreateBody(operationType: string, target: Record<string, unknown>, payload: Record<string, unknown>, reason: string, preview: ApprovalPreview | null, fallbackKey: string) {
  const canonicalPayload = preview?.canonicalBusinessInputs ?? payload;
  const payloadWithPreview = preview?.impactPreviewToken
    ? { ...canonicalPayload, impactPreviewToken: preview.impactPreviewToken }
    : canonicalPayload;
  return {
    operationType,
    target,
    reason,
    payload: payloadWithPreview,
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    idempotencyKey: preview?.idempotencyKey ?? fallbackKey,
  };
}

export function OperationsPage({ session }: { session: SessionView }) {
  const caps = operationsCapabilities(session.actions);
  const canReadOperations = hasAction(session, "health.read");
  const canManageApprovals = hasAction(session, "approvals.manage");
  const canRequestOperationDirectory = caps.showFlagActions || canManageApprovals;
  const currentOperator = session.admin;
  const loader = useCallback(async (): Promise<OperationsConsole> => {
    if (!canReadOperations) return { incidents: [], maintenance: [], flags: [], tenants: [], selectors: emptyOperationsSelectors };
    return normalizeOperationsConsole(await api("/api/operations"));
  }, [canReadOperations]);
  const approvalsLoader = useCallback(async (): Promise<ApprovalConsole> => {
    if (!canRequestOperationDirectory) return { operations: [], pendingCount: 0, executionApprovals: [] };
    const [directory, approvals] = await Promise.all([
      api("/api/approval-operations"),
      caps.showFlagActions || canManageApprovals ? api("/api/approvals") : Promise.resolve({ items: [] }),
    ]);
    return normalizeApprovalConsole(directory, approvals, { currentOperatorId: currentOperator.id, requesterOnlyList: !canManageApprovals });
  }, [canManageApprovals, canRequestOperationDirectory, caps.showFlagActions, currentOperator.id]);
  const remote = useRemote(loader);
  const approvalsRemote = useRemote(approvalsLoader);
  const data = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data : { incidents: [], maintenance: [], flags: [], tenants: [], selectors: emptyOperationsSelectors };
  const approvals = approvalsRemote.state.status === "ready" || approvalsRemote.state.status === "empty" ? approvalsRemote.state.data : { operations: [], pendingCount: 0, executionApprovals: [] };
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [incidentFormOpen, setIncidentFormOpen] = useState(false);
  const [incidentStatus, setIncidentStatus] = useState<IncidentStatusState>(null);
  const [maintenanceFormOpen, setMaintenanceFormOpen] = useState(false);
  const [maintenanceAction, setMaintenanceAction] = useState<MaintenanceActionState>(null);
  const [flagWizard, setFlagWizard] = useState<FlagWizardState>(null);
  const [flagExecution, setFlagExecution] = useState<FlagExecutionState>(null);

  function notifySuccess(text: string, reloadApprovals = false) {
    setFeedback({ tone: "success", text });
    void remote.reload();
    if (reloadApprovals) void approvalsRemote.reload();
  }

  return <div className="page operations-page">
    <PageHeader
      icon={ShieldAlert}
      title="运营治理"
      actions={<div className="inline-actions">
        {caps.showMaintenanceActions && <button type="button" className="button secondary" onClick={() => setMaintenanceFormOpen(true)}><CalendarClock size={16} />维护窗口</button>}
        {caps.showIncidentActions && <button type="button" className="button primary" onClick={() => setIncidentFormOpen(true)}><AlertTriangle size={16} />登记事故</button>}
      </div>}
    />
    {feedback && <div className={`feedback-bar ${feedback.tone}`} role="status"><span>{feedback.text}</span></div>}
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={!canReadOperations || (remote.state.status === "error" && remote.state.httpStatus === 403)
        ? { title: "当前账号不能查看运营治理", description: "请联系平台负责人开通运营查看权限；页面不会用空列表冒充真实状态。" }
        : null}
      emptyHint="当前没有开放事故、维护窗口或功能开关变更。需要变更时请从正式操作入口发起。"
    >{(readyData) => <div className="workspace-stack operations-workbench">
      {readyData.incidents.length > 0 && <IncidentsSection rows={readyData.incidents} canManage={caps.showIncidentActions} onTransition={(incident, status) => setIncidentStatus({ incident, status })} />}
      {readyData.maintenance.length > 0 && <MaintenanceSection rows={readyData.maintenance} canManage={caps.showMaintenanceActions} onAction={(item, mode) => setMaintenanceAction({ item, mode })} />}
      <FeatureFlagsSection
        rows={readyData.flags}
        incidents={readyData.incidents}
        maintenance={readyData.maintenance}
        approvalCount={approvals.pendingCount}
        executionApprovals={approvals.executionApprovals}
        canManage={caps.showFlagActions}
        onSchedule={() => setFlagWizard({ mode: "schedule", initialFlag: readyData.flags[0] ?? null })}
        onRollback={(flag) => setFlagWizard({ mode: "rollback", initialFlag: flag })}
        onExecute={(approval) => setFlagExecution(approval)}
      />
    </div>}</RemotePageState>
    {incidentFormOpen && <IncidentCreateForm selectors={data.selectors} onClose={() => setIncidentFormOpen(false)} onDone={() => { setIncidentFormOpen(false); notifySuccess("事故已登记，正在刷新列表。"); }} />}
    {incidentStatus && <IncidentStatusForm state={incidentStatus} selectors={data.selectors} onClose={() => setIncidentStatus(null)} onDone={() => { setIncidentStatus(null); notifySuccess("事故处置记录已更新。"); }} />}
    {maintenanceFormOpen && <MaintenanceCreateForm selectors={data.selectors} onClose={() => setMaintenanceFormOpen(false)} onDone={() => { setMaintenanceFormOpen(false); notifySuccess("维护窗口已提交，正在刷新列表。"); }} />}
    {maintenanceAction && <MaintenanceActionDialog state={maintenanceAction} onClose={() => setMaintenanceAction(null)} onDone={() => { setMaintenanceAction(null); notifySuccess(maintenanceAction.mode === "cancel" ? "维护取消已提交。" : "提前结束已提交。"); }} />}
    {flagWizard && <FlagCreateForm
      mode={flagWizard.mode}
      initialFlag={flagWizard.initialFlag}
      flags={data.flags}
      selectors={data.selectors}
      operations={approvals.operations}
      onClose={() => setFlagWizard(null)}
      onDone={() => { setFlagWizard(null); notifySuccess("功能开关审批申请已提交，等待另一位审批人通过后再执行。", true); }}
    />}
    {flagExecution && <FlagExecutionDialog
      approval={flagExecution}
      onClose={() => setFlagExecution(null)}
      onDone={() => { setFlagExecution(null); notifySuccess(flagExecution.operationType === "feature_flag.rollback" ? "功能回滚已执行，正在刷新列表。" : "功能发布已执行，正在刷新列表。", true); }}
    />}
  </div>;
}

function OperationsStatusSummary({ incidents, maintenance, pendingApprovals, executableChanges }: {
  incidents: IncidentView[];
  maintenance: MaintenanceView[];
  pendingApprovals: number;
  executableChanges: number;
}) {
  const openIncidents = incidents.filter((item) => item.status !== "resolved");
  const activeMaintenance = maintenance.filter((item) => item.status !== "cancelled" && item.status !== "completed");
  return <span className="ops-section-status" aria-label="运营状态概览">
    <span className={openIncidents.length > 0 ? "attention" : ""}><AlertTriangle size={16} /><strong>开放事故</strong><b className="app-number">{openIncidents.length}</b></span>
    <span className={activeMaintenance.length > 0 ? "scheduled" : ""}><CalendarClock size={16} /><strong>维护窗口</strong><b className="app-number">{activeMaintenance.length}</b></span>
    <span><FileCheck2 size={16} /><strong>待审批</strong><b className="app-number">{pendingApprovals}</b></span>
    <span className={executableChanges > 0 ? "ready" : ""}><Flag size={16} /><strong>待执行</strong><b className="app-number">{executableChanges}</b></span>
  </span>;
}

function IncidentsSection({ rows, canManage, onTransition }: {
  rows: IncidentView[];
  canManage: boolean;
  onTransition: (incident: IncidentView, status: "monitoring" | "resolved") => void;
}) {
  const columns: Array<DataTableColumn<IncidentView>> = [
    { key: "incident", header: "事故", width: "24%", render: (item) => <span className="ops-cell-main"><strong>{item.title}</strong><small>{statusLabel(severityLabels, item.severity, "级别待确认")} · {statusLabel(incidentStatusLabels, item.status, "状态待确认")}</small></span> },
    { key: "scope", header: "影响范围", width: "20%", render: (item) => item.scopeSummary },
    { key: "owner", header: "负责人", width: "16%", render: (item) => item.ownerName },
    { key: "timeline", header: "处置时间线", width: "20%", render: (item) => item.timelineSummary },
    { key: "recovery", header: "恢复记录", width: "20%", render: (item) => <span className="ops-cell-main"><small>{item.recoveryPlan}</small><small>复盘：{item.postmortem}</small></span> },
  ];
  return <SectionCard icon={AlertTriangle} title="事故中心" meta={<span><strong className="app-number">{rows.length}</strong> 个事故</span>} variant="ledger">
    <DataTable
      ariaLabel="事故中心"
      columns={columns}
      rows={rows}
      rowKey={(item) => item.id}
      empty={<span>当前无事故。出现客户影响时，请登记严重度、影响范围、负责人、恢复计划和复盘要求。</span>}
      rowActions={(item) => canManage ? <div className="row-action-group">
        {item.status === "open" && <button type="button" className="button secondary" onClick={() => onTransition(item, "monitoring")}>标记观察</button>}
        {item.status !== "resolved" && <button type="button" className="button primary" onClick={() => onTransition(item, "resolved")}>记录恢复</button>}
      </div> : <span className="ops-action-note">仅可查看</span>}
    />
  </SectionCard>;
}

function MaintenanceSection({ rows, canManage, onAction }: {
  rows: MaintenanceView[];
  canManage: boolean;
  onAction: (item: MaintenanceView, mode: "cancel" | "end-early") => void;
}) {
  const columns: Array<DataTableColumn<MaintenanceView>> = [
    { key: "window", header: "维护窗口", width: "24%", render: (item) => <span className="ops-cell-main"><strong>{item.title}</strong><small>{item.windowText}</small></span> },
    { key: "scope", header: "影响企业", width: "18%", render: (item) => item.scopeSummary },
    { key: "notice", header: "通知计划", width: "22%", render: (item) => item.notificationPlan },
    { key: "policy", header: "取消与提前结束", width: "22%", render: (item) => <span className="ops-cell-main"><small>{item.cancellation}</small><small>{item.earlyEnd}</small></span> },
    { key: "status", header: "状态", width: "14%", render: (item) => <span className="ops-cell-main"><StatusBadge value={operationBadgeValue(item.status)} /><small>{statusLabel(maintenanceStatusLabels, item.status, "状态待确认")}</small></span> },
  ];
  return <SectionCard icon={CalendarClock} title="维护计划" meta={<span><strong className="app-number">{rows.length}</strong> 个窗口</span>} variant="ledger">
    <DataTable
      ariaLabel="维护计划"
      columns={columns}
      rows={rows}
      rowKey={(item) => item.id}
      empty={<span>暂无维护计划。创建时需填写时间、影响企业、通知计划、取消与提前结束策略。</span>}
      rowActions={(item) => canManage ? <div className="row-action-group">
        <button type="button" className="button secondary" onClick={() => onAction(item, "cancel")}>取消维护</button>
        <button type="button" className="button primary" onClick={() => onAction(item, "end-early")}>提前结束</button>
      </div> : <span className="ops-action-note">仅可查看</span>}
    />
  </SectionCard>;
}

function FeatureFlagsSection({ rows, incidents, maintenance, approvalCount, executionApprovals, canManage, onSchedule, onRollback, onExecute }: {
  rows: FeatureFlagView[];
  incidents: IncidentView[];
  maintenance: MaintenanceView[];
  approvalCount: number;
  executionApprovals: FeatureFlagExecutionApproval[];
  canManage: boolean;
  onSchedule: () => void;
  onRollback: (flag: FeatureFlagView) => void;
  onExecute: (approval: FeatureFlagExecutionApproval) => void;
}) {
  const columns: Array<DataTableColumn<FeatureFlagView>> = [
    { key: "flag", header: "功能", width: "28%", render: (item) => <span className="ops-cell-main"><strong>{item.label}</strong><small>{item.description}</small><small>{environmentLabel(item.environment)}</small></span> },
    { key: "scope", header: "发布范围（租户组 / 名单）与时间", width: "27%", render: (item) => <span className="ops-cell-main"><strong>{item.scopeSummary}</strong><small>计划时间：{item.plannedAt}</small><small>自动过期：{item.autoExpireAt}</small></span> },
    { key: "rollout", header: "当前状态", width: "18%", render: (item) => <span className="ops-cell-main"><StatusBadge value={item.status === "unconfigured" ? "unchecked" : operationBadgeValue(item.status)} /><small>{flagStatusText(item)}</small><strong className="app-number">{flagRolloutText(item)}</strong></span> },
    { key: "rollback", header: "恢复方案", width: "27%", render: (item) => item.rollbackPlan },
  ];
  const approvalColumns: Array<DataTableColumn<FeatureFlagExecutionApproval>> = [
    { key: "approval", header: "已批准变更", width: "28%", render: (item) => <span className="ops-cell-main"><strong>{item.label}</strong><small>{item.summary}</small></span> },
    { key: "status", header: "审批状态", width: "20%", render: (item) => <span className="ops-cell-main"><StatusBadge value="active" /><small>{item.statusText}</small></span> },
    { key: "people", header: "申请与审批", width: "22%", render: (item) => <span className="ops-cell-main"><small>申请：{item.requestedByName}</small><small>批准：{item.approvedByName}</small></span> },
    { key: "recovery", header: "影响与恢复", width: "30%", render: (item) => <span className="ops-cell-main"><small>{item.impact[0]}</small><small>{item.recovery}</small></span> },
  ];
  return <SectionCard
    icon={Flag}
    title="功能开关"
    meta={<OperationsStatusSummary incidents={incidents} maintenance={maintenance} pendingApprovals={approvalCount} executableChanges={executionApprovals.length} />}
    variant="ledger"
    action={canManage ? <button type="button" className="button primary" title="只从业务目录选择变更对象" onClick={onSchedule}><Flag size={16} />计划功能变更</button> : undefined}
  >
    <DataTable
      ariaLabel="功能开关"
      columns={columns}
      rows={rows}
      rowKey={(item) => item.id}
      empty={<span>暂无功能开关目录。请等待平台返回正式目录和选择凭证，页面不会用手填编号发起变更。</span>}
      {...(canManage && rows.some((item) => item.status !== "unconfigured") ? {
        rowActions: (item: FeatureFlagView) => item.status === "unconfigured"
          ? <span className="ops-action-note">—</span>
          : <button type="button" className="button secondary" onClick={() => onRollback(item)}><RotateCcw size={15} />回滚</button>,
      } : {})}
    />
    {executionApprovals.length > 0 && <div className="ops-approved-queue">
      <h3>已批准待执行 <span className="app-number">{executionApprovals.length}</span></h3>
      <DataTable
        ariaLabel="待执行的已批准变更"
        columns={approvalColumns}
        rows={executionApprovals}
        rowKey={(item) => item.id}
        rowActions={(item) => canManage
          ? <div className="row-action-group">
            {item.unavailableReason
              ? <span className="ops-action-note">{item.unavailableReason}</span>
              : <button type="button" className="button primary" onClick={() => onExecute(item)}>{item.operationType === "feature_flag.rollback" ? "执行已批准回滚" : "执行已批准发布"}</button>}
          </div>
          : <span className="ops-action-note">需功能开关权限</span>}
      />
    </div>}
  </SectionCard>;
}

function IncidentCreateForm({ selectors, onClose, onDone }: {
  selectors: OperationsSelectors;
  onClose: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState("平台服务异常");
  const [severity, setSeverity] = useState("sev2");
  const initialScopeMode: FlagDraft["scopeMode"] = selectors.tenantGroups.length ? "tenant_group" : selectors.allowLists.length ? "allow_list" : "all_tenants";
  const [scopeMode, setScopeMode] = useState<FlagDraft["scopeMode"]>(initialScopeMode);
  const [scopeToken, setScopeToken] = useState(firstSelectableToken(scopeSelectorItems(initialScopeMode, selectors)));
  const [ownerToken, setOwnerToken] = useState(firstSelectableToken(selectors.owners));
  const [fallbackOwnerToken, setFallbackOwnerToken] = useState(firstSelectableToken(selectors.fallbackOwners));
  const [description, setDescription] = useState("");
  const [timelineNote, setTimelineNote] = useState("");
  const [recoveryPlan, setRecoveryPlan] = useState("");
  const [postmortemRequired, setPostmortemRequired] = useState("required");
  const [postmortemDue, setPostmortemDue] = useState(localDateTime(48));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestKey] = useState(() => createRequestKey("ops-incident"));
  const scopeChoices = useMemo(() => selectorOptions(scopeSelectorItems(scopeMode, selectors), "暂无可选影响范围"), [scopeMode, selectors]);
  const ownerChoices = useMemo(() => selectorOptions(selectors.owners, "暂无可选负责人"), [selectors.owners]);
  const fallbackOwnerChoices = useMemo(() => selectorOptions(selectors.fallbackOwners, "暂无可选备用负责人"), [selectors.fallbackOwners]);
  function changeScopeMode(value: string) {
    const nextMode = value as FlagDraft["scopeMode"];
    setScopeMode(nextMode);
    setScopeToken(firstSelectableToken(scopeSelectorItems(nextMode, selectors)));
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (description.trim().length < 12 || timelineNote.trim().length < 8 || recoveryPlan.trim().length < 8) {
      setError("请补齐事故描述、处置时间线和恢复计划，再登记事故。");
      return;
    }
    const scope = scopePayload(scopeMode, scopeToken, 100, selectors);
    if (!scope || !ownerToken || !fallbackOwnerToken) {
      setError("平台还没有返回可用的影响范围或负责人选择凭证，请刷新后重试。");
      return;
    }
    setBusy(true); setError("");
    try {
      await api("/api/operations/incidents", {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          severity,
          description: description.trim(),
          scope,
          ownerSelectionToken: ownerToken,
          timeline: [{ at: new Date().toISOString(), status: "detected", note: timelineNote.trim() }],
          recoveryPlan: { summary: recoveryPlan.trim(), fallbackOwnerSelectionToken: fallbackOwnerToken },
          postmortem: { required: postmortemRequired === "required", dueAt: toIsoFromLocal(postmortemDue), ownerSelectionToken: ownerToken },
          idempotencyKey: requestKey,
        }),
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "事故登记失败，请检查后重试。");
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title="登记事故" description="登记事故前，请说明影响范围、负责人、时间线、恢复计划和复盘要求。" onClose={onClose} wide>
    <form className="ops-form-grid" onSubmit={submit}>
      <label><span>事故标题</span><input value={title} onChange={(event) => setTitle(event.target.value)} required /></label>
      <div className="field-block"><span>事故严重度</span><V3Select value={severity} onChange={setSeverity} options={severityOptions} ariaLabel="事故严重度" /></div>
      <div className="field-block"><span>影响范围类型</span><V3Select value={scopeMode} onChange={changeScopeMode} options={scopeOptions} ariaLabel="影响范围类型" /></div>
      <div className="field-block"><span>影响范围</span><V3Select value={scopeToken} onChange={setScopeToken} options={scopeChoices} ariaLabel="影响范围" /></div>
      <div className="field-block"><span>负责人</span><V3Select value={ownerToken} onChange={setOwnerToken} options={ownerChoices} ariaLabel="负责人" /></div>
      <div className="field-block"><span>备用负责人</span><V3Select value={fallbackOwnerToken} onChange={setFallbackOwnerToken} options={fallbackOwnerChoices} ariaLabel="备用负责人" /></div>
      <label className="full"><span>事故描述</span><textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="请说明异常表现、开始时间和客户影响" /></label>
      <label className="full"><span>处置时间线</span><textarea rows={3} value={timelineNote} onChange={(event) => setTimelineNote(event.target.value)} placeholder="例如：监控告警触发，已暂停扩大影响的操作" /></label>
      <label className="full"><span>恢复计划</span><textarea rows={3} value={recoveryPlan} onChange={(event) => setRecoveryPlan(event.target.value)} placeholder="例如：切到备用队列，恢复后验证核心链路" /></label>
      <div className="field-block"><span>复盘</span><V3Select value={postmortemRequired} onChange={setPostmortemRequired} options={[{ value: "required", label: "需要复盘" }, { value: "optional", label: "暂不要求复盘" }]} ariaLabel="复盘" /></div>
      <label><span>复盘截止时间</span><input type="datetime-local" value={postmortemDue} onChange={(event) => setPostmortemDue(event.target.value)} /></label>
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
      <footer className="dialog-footer full"><button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button><button className="button primary" disabled={busy}>{busy ? "正在登记…" : "登记事故"}</button></footer>
    </form>
  </Dialog>;
}

function IncidentStatusForm({ state, selectors, onClose, onDone }: {
  state: NonNullable<IncidentStatusState>;
  selectors: OperationsSelectors;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [recoveryEvidence, setRecoveryEvidence] = useState("");
  const [ownerToken, setOwnerToken] = useState(firstSelectableToken(selectors.owners));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestKey] = useState(() => createRequestKey("ops-incident-status"));
  const ownerChoices = useMemo(() => selectorOptions(selectors.owners, "暂无可选负责人"), [selectors.owners]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (note.trim().length < 8 || (state.status === "resolved" && recoveryEvidence.trim().length < 8)) {
      setError("请补齐处置说明和恢复证据。");
      return;
    }
    if (!ownerToken) {
      setError("平台还没有返回可用负责人选择凭证，请刷新后重试。");
      return;
    }
    setBusy(true); setError("");
    try {
      await api(`/api/operations/incidents/${state.incident.id}/status`, {
        method: "POST",
        body: JSON.stringify({
          status: state.status,
          timeline: [{ at: new Date().toISOString(), status: state.status, note: note.trim(), ownerSelectionToken: ownerToken }],
          recoveryEvidence: recoveryEvidence.trim() || undefined,
          idempotencyKey: requestKey,
        }),
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "事故处置更新失败，请重试。");
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={state.status === "resolved" ? "记录恢复" : "标记观察"} description={`事故：${state.incident.title}`} onClose={onClose}>
    <form className="ops-form-grid" onSubmit={submit}>
      <div className="field-block full"><span>负责人</span><V3Select value={ownerToken} onChange={setOwnerToken} options={ownerChoices} ariaLabel="负责人" /></div>
      <label className="full"><span>处置时间线</span><textarea rows={4} value={note} onChange={(event) => setNote(event.target.value)} placeholder="请说明当前处理进展" /></label>
      {state.status === "resolved" && <label className="full"><span>恢复记录</span><textarea rows={4} value={recoveryEvidence} onChange={(event) => setRecoveryEvidence(event.target.value)} placeholder="请说明已验证恢复的业务证据" /></label>}
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
      <footer className="dialog-footer full"><button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button><button className="button primary" disabled={busy}>{busy ? "正在提交…" : "提交处置记录"}</button></footer>
    </form>
  </Dialog>;
}

function MaintenanceCreateForm({ selectors, onClose, onDone }: {
  selectors: OperationsSelectors;
  onClose: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState("平台例行维护");
  const initialScopeMode: FlagDraft["scopeMode"] = selectors.tenantGroups.length ? "tenant_group" : selectors.allowLists.length ? "allow_list" : "all_tenants";
  const [scopeMode, setScopeMode] = useState<FlagDraft["scopeMode"]>(initialScopeMode);
  const [scopeToken, setScopeToken] = useState(firstSelectableToken(scopeSelectorItems(initialScopeMode, selectors)));
  const [startsAt, setStartsAt] = useState(localDateTime(1));
  const [endsAt, setEndsAt] = useState(localDateTime(2));
  const [message, setMessage] = useState("");
  const [notificationPlan, setNotificationPlan] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestKey] = useState(() => createRequestKey("ops-maintenance"));
  const scopeChoices = useMemo(() => selectorOptions(scopeSelectorItems(scopeMode, selectors), "暂无可选影响企业"), [scopeMode, selectors]);
  function changeScopeMode(value: string) {
    const nextMode = value as FlagDraft["scopeMode"];
    setScopeMode(nextMode);
    setScopeToken(firstSelectableToken(scopeSelectorItems(nextMode, selectors)));
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (message.trim().length < 12 || notificationPlan.trim().length < 8) {
      setError("请补齐维护说明和通知计划。");
      return;
    }
    const startIso = toIsoFromLocal(startsAt);
    const endIso = toIsoFromLocal(endsAt);
    const scope = scopePayload(scopeMode, scopeToken, 100, selectors);
    if (!scope) {
      setError("平台还没有返回可用的影响企业选择凭证，请刷新后重试。");
      return;
    }
    setBusy(true); setError("");
    try {
      await api("/api/operations/maintenance", {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          startsAt: startIso,
          endsAt: endIso,
          message: message.trim(),
          window: { startsAt: startIso, endsAt: endIso, timezone: "Asia/Shanghai" },
          scope,
          notificationPlan: { channels: ["email", "in_app"], firstNoticeAt: startIso, reminderAt: startIso, message: notificationPlan.trim() },
          cancellation: { allowedUntil: startIso, reasonRequired: true },
          earlyEnd: { allowedAfterStart: true, recoveryRequired: true },
          idempotencyKey: requestKey,
        }),
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "维护窗口提交失败，请检查后重试。");
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title="创建维护窗口" description="需要明确时间、影响企业、通知计划、取消和提前结束策略。" onClose={onClose} wide>
    <form className="ops-form-grid" onSubmit={submit}>
      <label><span>维护标题</span><input value={title} onChange={(event) => setTitle(event.target.value)} required /></label>
      <div className="field-block"><span>影响企业类型</span><V3Select value={scopeMode} onChange={changeScopeMode} options={scopeOptions} ariaLabel="影响企业类型" /></div>
      <div className="field-block"><span>影响企业</span><V3Select value={scopeToken} onChange={setScopeToken} options={scopeChoices} ariaLabel="影响企业" /></div>
      <label><span>开始时间</span><input type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} /></label>
      <label><span>结束时间</span><input type="datetime-local" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} /></label>
      <label className="full"><span>维护说明</span><textarea rows={3} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="请说明维护内容和业务影响" /></label>
      <label className="full"><span>通知计划</span><textarea rows={3} value={notificationPlan} onChange={(event) => setNotificationPlan(event.target.value)} placeholder="请写明提前通知、窗口开始和恢复通知安排" /></label>
      <div className="ops-form-note full"><span>取消维护：开始前必须说明原因并通知企业。</span><span>提前结束：需要恢复验证记录。</span></div>
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
      <footer className="dialog-footer full"><button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button><button className="button primary" disabled={busy}>{busy ? "正在提交…" : "提交维护窗口"}</button></footer>
    </form>
  </Dialog>;
}

function MaintenanceActionDialog({ state, onClose, onDone }: {
  state: NonNullable<MaintenanceActionState>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [recoveryEvidence, setRecoveryEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [requestKey] = useState(() => createRequestKey("ops-maintenance-action"));
  async function submit() {
    if (reason.trim().length < 8 || (state.mode === "end-early" && recoveryEvidence.trim().length < 8)) {
      setError("请补齐原因和恢复证据。");
      return;
    }
    setBusy(true); setError("");
    try {
      await api(`/api/operations/maintenance/${state.item.id}/${state.mode === "cancel" ? "cancel" : "end-early"}`, {
        method: "POST",
        body: JSON.stringify({
          reason: reason.trim(),
          recoveryEvidence: recoveryEvidence.trim() || undefined,
          notificationPlan: { channels: ["email", "in_app"], message: state.mode === "cancel" ? "维护取消通知" : "维护提前结束通知" },
          idempotencyKey: requestKey,
        }),
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "维护操作提交失败，请重试。");
    } finally {
      setBusy(false);
    }
  }
  return <ConfirmDialog
    title={state.mode === "cancel" ? "取消维护" : "提前结束维护"}
    description={state.item.title}
    confirmLabel={state.mode === "cancel" ? "提交取消" : "提交提前结束"}
    onConfirm={() => void submit()}
    onClose={onClose}
    busy={busy}
    confirmDisabled={reason.trim().length < 8 || (state.mode === "end-early" && recoveryEvidence.trim().length < 8)}
  >
    <div className="ops-form-grid">
      <label className="full"><span>原因</span><textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="请说明为什么要调整维护计划" /></label>
      {state.mode === "end-early" && <label className="full"><span>恢复记录</span><textarea rows={3} value={recoveryEvidence} onChange={(event) => setRecoveryEvidence(event.target.value)} placeholder="请说明哪些业务链路已验证恢复" /></label>}
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
    </div>
  </ConfirmDialog>;
}

export function FlagCreateForm({ mode = "schedule", initialFlag = null, flags = [], selectors = emptyOperationsSelectors, operations = [], onClose, onDone, onSubmit }: {
  mode?: FlagWizardMode;
  initialFlag?: FeatureFlagView | null;
  flags?: FeatureFlagView[];
  selectors?: OperationsSelectors;
  operations?: OperationDirectoryItem[];
  onClose: () => void;
  onDone?: () => void;
  onSubmit?: (draft: FlagDraft) => void;
}) {
  const operationChoices = operationOptions(operations, mode);
  const initialOperationType = operationChoices[0]?.value ?? "";
  const initialFlagValue = initialFlag ? flagChoiceValue(initialFlag) : firstSelectableFlagValue(flags, initialOperationType);
  const [approvalRequestKey] = useState(() => createRequestKey("ops-approval"));
  const [draft, setDraft] = useState<FlagDraft>(() => ({
    operationType: initialOperationType,
    flagCatalogKey: initialFlagValue,
    environment: initialFlag?.environment ?? "production",
    scopeMode: "tenant_group",
    scopeSelectionToken: firstSelectableToken(scopeSelectorItems("tenant_group", selectors)),
    rolloutPercent: mode === "rollback" ? "0" : "25",
    plannedAt: localDateTime(1),
    autoExpireAt: localDateTime(25),
    reason: "",
    preview: null,
    previewLoading: false,
    previewError: "",
    submitting: false,
    submitError: "",
  }));
  const flagChoices = flagOptions(flags, draft.operationType);
  const selectedFlag = flags.find((flag) => flagChoiceValue(flag) === draft.flagCatalogKey) ?? initialFlag ?? null;
  const selectedFlagToken = flagSelectionTokenFor(selectedFlag, draft.operationType);
  const selectedOperation = operations.find((operation) => operation.operationType === draft.operationType) ?? null;
  const selectedRollout = Number(draft.rolloutPercent);
  const scopeChoices = useMemo(() => selectorOptions(scopeSelectorItems(draft.scopeMode, selectors), "暂无可选发布范围"), [draft.scopeMode, selectors]);

  function changeOperation(value: string) {
    const nextFlagValue = selectedFlag && flagSelectionTokenFor(selectedFlag, value)
      ? flagChoiceValue(selectedFlag)
      : firstSelectableFlagValue(flags, value);
    const nextFlag = flags.find((flag) => flagChoiceValue(flag) === nextFlagValue) ?? null;
    setDraft((current) => ({
      ...current,
      operationType: value,
      flagCatalogKey: nextFlagValue,
      environment: nextFlag?.environment ?? current.environment,
      preview: null,
      previewError: "",
      submitError: "",
    }));
  }

  function changeFlag(value: string) {
    const nextFlag = flags.find((flag) => flagChoiceValue(flag) === value) ?? null;
    setDraft((current) => ({
      ...current,
      flagCatalogKey: value,
      environment: nextFlag?.environment ?? current.environment,
      preview: null,
      previewError: "",
      submitError: "",
    }));
  }

  function changeEnvironment(value: string) {
    const nextFlag = selectedFlag
      ? flags.find((flag) => flag.catalogKey === selectedFlag.catalogKey && flag.environment === value)
      : flags.find((flag) => flag.environment === value && flagSelectionTokenFor(flag, draft.operationType));
    setDraft((current) => ({
      ...current,
      environment: value,
      flagCatalogKey: nextFlag ? flagChoiceValue(nextFlag) : "",
      preview: null,
      previewError: "",
      submitError: "",
    }));
  }

  function changeScopeMode(value: string) {
    const nextMode = value as FlagDraft["scopeMode"];
    setDraft((current) => ({
      ...current,
      scopeMode: nextMode,
      scopeSelectionToken: firstSelectableToken(scopeSelectorItems(nextMode, selectors)),
      preview: null,
      previewError: "",
      submitError: "",
    }));
  }

  function setDraftValue<K extends keyof FlagDraft>(key: K, value: FlagDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value, preview: null, previewError: "", submitError: "" }));
  }

  function flagPayload(): Record<string, unknown> {
    const plannedAt = toIsoFromLocal(draft.plannedAt);
    const autoExpireAt = toIsoFromLocal(draft.autoExpireAt);
    const rolloutPercent = Number.isFinite(selectedRollout) ? selectedRollout : 0;
    const scope = scopePayload(draft.scopeMode, draft.scopeSelectionToken, rolloutPercent, selectors);
    return {
      operation: mode === "rollback" ? "rollback" : rolloutPercent === 100 ? "full_publish" : "schedule_change",
      catalogKey: selectedFlag?.catalogKey,
      environment: draft.environment,
      enabled: rolloutPercent > 0,
      rolloutPercent,
      previousRolloutPercent: selectedFlag?.rolloutPercent ?? 0,
      scope,
      plannedAt,
      autoExpireAt,
      schedule: { plannedAt, autoExpireAt },
      rollbackPlan: { mode: "restore_previous", previousRolloutPercent: selectedFlag?.rolloutPercent ?? 0, expireAt: autoExpireAt },
      ...(rolloutPercent === 100 ? { impactScope: draft.scopeMode === "all_tenants" ? "all_targets" : "tenant", acknowledgedFullReleaseImpact: true } : {}),
    };
  }

  function validate(): string | null {
    if (!draft.operationType || !selectedOperation) return "审批目录还没有返回可执行的功能开关动作。";
    if (selectedOperation.disabled) return selectedOperation.unavailableReason || "当前功能变更暂不可发起。";
    if (!draft.flagCatalogKey) return "请选择正式功能目录中的功能。";
    if (!selectedFlag) return "请选择正式功能目录中的功能。";
    if (!selectedFlagToken) return "平台还没有返回该功能与当前动作匹配的选择凭证，请刷新后重试。";
    if (!isCatalogKey(selectedFlag.catalogKey)) return "平台还没有返回正式功能目录，请刷新后重试。";
    if (!scopePayload(draft.scopeMode, draft.scopeSelectionToken, Number.isFinite(selectedRollout) ? selectedRollout : 0, selectors)) return "请选择平台返回的发布范围。";
    if (draft.reason.trim().length < 12) return "申请原因至少需要 12 个字，请说明业务背景和恢复方式。";
    return null;
  }

  async function preview() {
    if (draft.previewLoading || draft.submitting) return;
    const failure = validate();
    if (failure) { setDraft((current) => ({ ...current, previewError: failure })); return; }
    if (!selectedFlagToken) { setDraft((current) => ({ ...current, previewError: "平台还没有返回该功能与当前动作匹配的选择凭证，请刷新后重试。" })); return; }
    setDraft((current) => ({ ...current, previewLoading: true, previewError: "", submitError: "" }));
    try {
      const result = await api<ApprovalPreview>("/api/approvals/preview", {
        method: "POST",
        body: JSON.stringify({
          operationType: draft.operationType,
          target: { selectionToken: selectedFlagToken },
          payload: flagPayload(),
        }),
      });
      setDraft((current) => ({ ...current, preview: result, previewLoading: false }));
    } catch (failure) {
      setDraft((current) => ({ ...current, previewLoading: false, previewError: failure instanceof Error ? failure.message : "影响预览生成失败，请重试。" }));
    }
  }

  async function submit() {
    if (draft.submitting) return;
    const failure = validate();
    if (failure || !draft.preview) { setDraft((current) => ({ ...current, submitError: failure ?? "请先生成影响预览。" })); return; }
    if (!selectedFlagToken) { setDraft((current) => ({ ...current, submitError: "平台还没有返回该功能与当前动作匹配的选择凭证，请刷新后重试。" })); return; }
    if (draft.operationType === "feature_flag.schedule_change" && !isOpsImpactPreviewToken(draft.preview.impactPreviewToken ?? null)) {
      setDraft((current) => ({ ...current, submitError: "影响预览尚未返回可用于后续执行的凭证，请重新生成预览。" }));
      return;
    }
    if (onSubmit) onSubmit(draft);
    setDraft((current) => ({ ...current, submitting: true, submitError: "" }));
    try {
      await api("/api/approvals", {
        method: "POST",
        body: JSON.stringify(approvalCreateBody(draft.operationType, { selectionToken: selectedFlagToken }, flagPayload(), draft.reason.trim(), draft.preview, approvalRequestKey)),
      });
      onDone?.();
    } catch (failure) {
      setDraft((current) => ({ ...current, submitting: false, submitError: failure instanceof Error ? failure.message : "审批申请提交失败，请重试。" }));
    }
  }

  const steps: WizardStep[] = [
    {
      key: "directory",
      title: "选择目录",
      summary: "功能开关只能从正式业务目录选择，不需要手动填写任何编号。",
      validate: () => (!draft.operationType || !selectedOperation ? "审批目录还没有可执行动作。" : selectedOperation.disabled ? selectedOperation.unavailableReason : null),
      content: <div className="ops-form-grid">
        <div className="field-block"><span>业务动作</span><V3Select value={draft.operationType} onChange={changeOperation} options={operationChoices} ariaLabel="业务动作" placeholder="等待审批目录" disabled={operationChoices.length === 0} /></div>
        <div className="field-block"><span>正式功能目录</span><V3Select value={draft.flagCatalogKey} onChange={changeFlag} options={flagChoices} ariaLabel="正式功能目录" placeholder="暂无功能可选" disabled={flagChoices.length === 0} /></div>
        {operationChoices.length === 0 && <InlineFeedback tone="warning">审批目录暂不可用，暂不能发起功能变更。</InlineFeedback>}
      </div>,
    },
    {
      key: "scope",
      title: "发布计划",
      summary: "选择环境、租户组或名单、发布比例、计划时间和自动过期时间。",
      validate,
      content: <div className="ops-form-grid">
        <div className="field-block"><span>环境</span><V3Select value={draft.environment} onChange={changeEnvironment} options={environmentOptions} ariaLabel="环境" /></div>
        <div className="field-block"><span>租户组 / 名单</span><V3Select value={draft.scopeMode} onChange={changeScopeMode} options={scopeOptions} ariaLabel="租户组或名单" /></div>
        <div className="field-block"><span>发布范围</span><V3Select value={draft.scopeSelectionToken} onChange={(value) => setDraftValue("scopeSelectionToken", value)} options={scopeChoices} ariaLabel="发布范围" placeholder="请选择发布范围" /></div>
        <div className="field-block"><span>发布比例</span><V3Select value={draft.rolloutPercent} onChange={(value) => setDraftValue("rolloutPercent", value)} options={rolloutOptions} ariaLabel="发布比例" /></div>
        <label><span>计划时间</span><input type="datetime-local" value={draft.plannedAt} onChange={(event) => setDraftValue("plannedAt", event.target.value)} /></label>
        <label><span>自动过期</span><input type="datetime-local" value={draft.autoExpireAt} onChange={(event) => setDraftValue("autoExpireAt", event.target.value)} /></label>
        <label className="full"><span>申请原因</span><textarea rows={4} value={draft.reason} onChange={(event) => setDraftValue("reason", event.target.value)} placeholder="请说明为什么发布、影响哪些企业、如何回滚" /></label>
      </div>,
    },
    {
      key: "preview",
      title: "影响预览",
      summary: "提交前必须先生成影响预览；预览失败不会创建审批申请。",
      content: <div className="ops-form-grid">
        <button type="button" className="button secondary" onClick={() => void preview()} disabled={draft.previewLoading || draft.submitting}><FileCheck2 size={16} />{draft.previewLoading ? "正在生成预览…" : "生成影响预览"}</button>
        {draft.previewError && <InlineFeedback tone="danger">{draft.previewError}</InlineFeedback>}
        {draft.preview ? <div className="ops-preview-card">
          <strong>影响预览已生成</strong>
          <ul>{(draft.preview.impact ?? ["影响范围待平台确认。"]).map((item) => <li key={item}>{safeText(item, "影响范围待确认。")}</li>)}</ul>
          <span>恢复方式：{safeText(draft.preview.recovery, "可回滚到上一发布比例。")}</span>
        </div> : <div className="ops-preview-card muted"><strong>等待影响预览</strong><span>没有预览前不会提交审批，也不会显示为已完成。</span></div>}
      </div>,
    },
  ];

  return <Wizard
    title={mode === "rollback" ? "发起功能回滚审批" : "计划功能变更"}
    description="功能开关变更必须经过影响预览与审批，定时发布和自动过期都以平台结果为准。"
    steps={steps}
    submitLabel="提交审批申请"
    onSubmit={submit}
    onClose={onClose}
    submitting={draft.submitting}
    submitDisabled={!draft.preview || draft.previewLoading || draft.submitting}
    submitError={draft.submitError}
  />;
}

function buildFeatureFlagExecutionBody(approval: FeatureFlagExecutionApproval, reason: string, requestKey: string): { path: string; method: "POST" | "PUT"; body: Record<string, unknown>; error: string } {
  const payload = approval.payload;
  if (approval.operationType === "feature_flag.rollback") {
    if (reason.trim().length < 8) return { path: "", method: "POST", body: {}, error: "请补充回滚原因，便于后续审计和恢复。" };
    return {
      path: "/api/operations/feature-flags/rollback",
      method: "POST",
      body: {
        catalogKey: payload.catalogKey,
        environment: payload.environment,
        rollbackPlan: payload.rollbackPlan,
        approvalId: approval.id,
        reason: reason.trim(),
        idempotencyKey: requestKey,
      },
      error: "",
    };
  }
  if (!payload.scope || !payload.schedule || !payload.impactPreviewToken) {
    return { path: "", method: "PUT", body: {}, error: "该审批缺少发布范围、计划时间或影响预览凭证，请重新发起。" };
  }
  return {
    path: "/api/operations/feature-flags",
    method: "PUT",
    body: {
      catalogKey: payload.catalogKey,
      environment: payload.environment,
      scope: payload.scope,
      schedule: payload.schedule,
      impactPreviewToken: payload.impactPreviewToken,
      approvalId: approval.id,
      rollbackPlan: payload.rollbackPlan,
      idempotencyKey: requestKey,
    },
    error: "",
  };
}

function FlagExecutionDialog({ approval, onClose, onDone }: {
  approval: FeatureFlagExecutionApproval;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState(approval.payload.reason ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(approval.unavailableReason);
  const [requestKey] = useState(() => createRequestKey(approval.operationType === "feature_flag.rollback" ? "ops-flag-rollback" : "ops-flag-execute"));
  const isRollback = approval.operationType === "feature_flag.rollback";
  async function submit() {
    if (busy) return;
    const request = buildFeatureFlagExecutionBody(approval, reason, requestKey);
    if (request.error) {
      setError(request.error);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api(request.path, {
        method: request.method,
        body: JSON.stringify(request.body),
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "执行失败，请检查后重试。");
    } finally {
      setBusy(false);
    }
  }
  return <ConfirmDialog
    title={isRollback ? "执行已批准回滚" : "执行已批准发布"}
    description="只会执行当前已批准、未过期、未执行的变更；执行结果以平台刷新后的状态为准。"
    confirmLabel={isRollback ? "确认执行回滚" : "确认执行发布"}
    onConfirm={() => void submit()}
    onClose={onClose}
    busy={busy}
    confirmDisabled={busy || Boolean(approval.unavailableReason) || (isRollback && reason.trim().length < 8)}
  >
    <div className="ops-form-grid">
      <div className="ops-preview-card full">
        <strong>{approval.summary}</strong>
        <ul>{approval.impact.map((item) => <li key={item}>{item}</li>)}</ul>
        <span>恢复方式：{approval.recovery}</span>
        <span>{approval.statusText}</span>
      </div>
      {isRollback && <label className="full"><span>回滚原因</span><textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="请说明为什么现在执行回滚，以及如何验证恢复" /></label>}
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
    </div>
  </ConfirmDialog>;
}
